import type {
  JobRequirement,
  JobSchedule,
  JobRequirementKind,
} from './types';

export const OIL_BARREL_TO_CUBIC_METRE = 0.158987294928;
export const DEFAULT_DELIVERY_PERIOD_S = 30 * 24 * 60 * 60;

function positive(value: number | null): value is number {
  return value !== null && Number.isFinite(value) && value > 0;
}

export function canonicalDemandFlow(
  requirement: JobRequirement,
): number | null {
  switch (requirement.kind) {
    case 'direct_flow':
      return positive(requirement.direct_flow_m3_s)
        ? requirement.direct_flow_m3_s
        : null;
    case 'barrels_per_minute':
      return positive(requirement.barrels_per_minute)
        ? requirement.barrels_per_minute * OIL_BARREL_TO_CUBIC_METRE / 60
        : null;
    case 'barrels_per_hour':
      return positive(requirement.barrels_per_hour)
        ? requirement.barrels_per_hour * OIL_BARREL_TO_CUBIC_METRE / 3600
        : null;
    case 'total_barrels':
      return positive(requirement.total_barrels) &&
        positive(requirement.delivery_period_s) &&
        positive(requirement.expected_uptime_fraction) &&
        requirement.expected_uptime_fraction <= 1
        ? requirement.total_barrels *
            OIL_BARREL_TO_CUBIC_METRE /
            (requirement.delivery_period_s *
              requirement.expected_uptime_fraction)
        : null;
  }
}

export function requirementForKind(
  kind: JobRequirementKind,
  currentFlowM3S: number | null,
  currentSchedule?: Partial<JobSchedule>,
): JobRequirement {
  const flow =
    currentFlowM3S !== null &&
    Number.isFinite(currentFlowM3S) &&
    currentFlowM3S > 0
      ? currentFlowM3S
      : null;
  const requestedPeriod = currentSchedule?.delivery_period_s ?? null;
  const requestedUptime = currentSchedule?.expected_uptime_fraction ?? null;
  const deliveryPeriodS = positive(requestedPeriod)
    ? requestedPeriod
    : DEFAULT_DELIVERY_PERIOD_S;
  const expectedUptime =
    positive(requestedUptime) && requestedUptime <= 1 ? requestedUptime : 1;
  const schedule: JobSchedule = {
    delivery_period_s: deliveryPeriodS,
    expected_uptime_fraction: expectedUptime,
  };
  switch (kind) {
    case 'direct_flow':
      return { ...schedule, direct_flow_m3_s: flow, kind };
    case 'barrels_per_minute':
      return {
        ...schedule,
        barrels_per_minute:
          flow === null ? null : flow * 60 / OIL_BARREL_TO_CUBIC_METRE,
        kind,
      };
    case 'barrels_per_hour':
      return {
        ...schedule,
        barrels_per_hour:
          flow === null ? null : flow * 3600 / OIL_BARREL_TO_CUBIC_METRE,
        kind,
      };
    case 'total_barrels':
      return {
        ...schedule,
        kind,
        total_barrels:
          flow === null
            ? null
            : flow *
              deliveryPeriodS *
              expectedUptime /
              OIL_BARREL_TO_CUBIC_METRE,
      };
  }
}
